/**
 * JSON Parser Utility
 * 
 * Transforms raw JSON project data into a normalized format for the application.
 * Handles time conversions, region processing, and data extraction.
 */

import { millisecondsToSeconds, calculateBarDuration, secondsToBars } from './timeConversion';

/**
 * Parse raw JSON project data into normalized format
 * 
 * @param {object} rawData - Raw JSON data from uploaded file
 * @returns {object} Normalized project data
 */
export function parseProjectData(rawData) {
  const { header, musician_tracks } = rawData;
  
  if (!musician_tracks || musician_tracks.length === 0) {
    throw new Error('No musician tracks found in project data');
  }
  
  const musicianTrack = musician_tracks[0];
  const barDuration = calculateBarDuration(header.tempo, header.time_signature);
  
  // Parse all regions
  const regions = parseRegions(musicianTrack.regions, barDuration);
  
  return {
    // Song metadata
    songName: header.project_name,
    songId: header.project_id,
    bpm: header.tempo,
    timeSignature: header.time_signature,
    musicKey: header.main_key?.chord_string || 'N/A',
    
    // Musician info
    musicianId: musicianTrack.musician_id,
    musicianName: musicianTrack.musician_name,
    
    // Regions data
    regions,
  };
}

/**
 * Parse region array from musician track
 * Converts timestamps to bars and normalizes region structure
 * 
 * @param {Array<object>} rawRegions - Raw region data from JSON
 * @param {number} barDuration - Duration of one bar in seconds
 * @returns {Array<object>} Parsed regions with bar positions
 */
function parseRegions(rawRegions, barDuration) {
  return rawRegions.map(region => ({
    // Identification
    id: region.id,
    type: normalizeRegionType(region.region_type),
    
    // Timing (converted to bars)
    start: secondsToBars(
      millisecondsToSeconds(region.start_time),
      barDuration
    ),
    end: secondsToBars(
      millisecondsToSeconds(region.end_time),
      barDuration
    ),
    
    // Performance parameters
    knobs: region.performance_parameters.knobs,
    group1: region.performance_parameters.groups?.groups1_parameters || [],
    group2: region.performance_parameters.groups?.groups2_parameters || [],
    group3: region.performance_parameters.groups?.groups3_parameters || [],
    
    // Custom parameters
    specialEffects: region.performance_parameters.custom_parameter_state,
    customInterface: region.performance_parameters.custom_parameter_interface,
  }));
}

/**
 * Normalize region type names
 * Handles special cases like 'break-auto' → 'break'
 * 
 * @param {string} regionType - Raw region type from JSON
 * @returns {string} Normalized region type
 */
function normalizeRegionType(regionType) {
  if (regionType === 'break-auto') {
    return 'break';
  }
  return regionType;
}

/**
 * Validate JSON structure before parsing
 * 
 * @param {object} data - Raw JSON data
 * @returns {{valid: boolean, error: string|null}}
 */
export function validateProjectData(data) {
  if (!data) {
    return { valid: false, error: 'No data provided' };
  }
  
  if (!data.header) {
    return { valid: false, error: 'Missing header section' };
  }
  
  if (!data.musician_tracks || !Array.isArray(data.musician_tracks)) {
    return { valid: false, error: 'Missing or invalid musician_tracks' };
  }
  
  if (data.musician_tracks.length === 0) {
    return { valid: false, error: 'No musician tracks found' };
  }
  
  const track = data.musician_tracks[0];
  if (!track.regions || !Array.isArray(track.regions)) {
    return { valid: false, error: 'No regions found in musician track' };
  }
  
  return { valid: true, error: null };
}


